import { useEffect, useRef } from 'react';
import { drawImage, drawShape } from '../utils/drawingUtils';
import { calculateBaseScale } from '../utils/shapeUtils';

export const useCanvas = (image, layers, currentShape, position, scale, editingLayer) => {
    const canvasRef = useRef(null);

    // Handle canvas setup and DPI scaling
    useEffect(() => {
        if (canvasRef.current && image) {
            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            
            // Set the canvas size accounting for DPI
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            
            // Scale the context to account for DPI
            const ctx = canvas.getContext('2d');
            ctx.scale(dpr, dpr);
            
            redrawCanvas();
        }
    }, [image]);

    // Handle redrawing when layers or current shape changes
    useEffect(() => {
        redrawCanvas();
    }, [layers, currentShape, position, scale, editingLayer]);

    const redrawCanvas = () => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate base scale
        const imageLayer = layers.find(layer => layer.type === 'image');
        const baseScale = calculateBaseScale(rect, imageLayer);
        
        // Calculate center position
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        // Add console.log to debug
        console.log('Drawing with editingLayer:', editingLayer);
        
        // Draw each visible layer
        layers.forEach(layer => {
            if (!layer.visible) return;
            
            console.log('Drawing layer:', layer);
            
            if (layer.type === 'image') {
                drawImage(ctx, layer.data, rect, scale, position);
            } else if (layer.type === 'shape' || layer.type === 'textbox') {
                ctx.save();
                
                // Apply transformations: translate to center, apply position offset, scale
                ctx.translate(centerX + position.x, centerY + position.y);
                ctx.scale(baseScale * scale, baseScale * scale);
                
                // For textbox, pass the layer directly since data is at root level
                const shapeData = layer.type === 'textbox' ? layer : layer.data;
                console.log('Shape data to draw:', shapeData);
                
                drawShape(ctx, shapeData, scale, false, layer.id, editingLayer);
                
                ctx.restore();
            }
        });

        // Draw current shape if exists
        if (currentShape) {
            ctx.save();
            ctx.translate(centerX + position.x, centerY + position.y);
            ctx.scale(baseScale * scale, baseScale * scale);
            drawShape(ctx, currentShape, scale, true);
            ctx.restore();
        }
    };

    return {
        canvasRef,
        redrawCanvas
    };
}; 